import React, { useState } from 'react';
import { getAppLocale } from '../../utils/locale';
import { CURRENCY_NOT_SET } from '../../utils/currency';
import { useMoney } from '../../hooks/useMoney';
import Icon from '../Icon';
import SearchableSelect from './SearchableSelect';
import { CurrencyInput } from './CurrencyInput';
import RichTextEditor from './RichTextEditor';
import '../../styles/components/LineItemsEditor.css';

export interface LineItemFieldConfig {
  key: string;
  type: 'picker' | 'text' | 'richtext' | 'number' | 'number-unit' | 'select' | 'currency' | 'checkbox' | 'photo' | 'picker-or-text' | 'readonly' | 'date';
  placeholder?: string;
  /** Column header shown above row-1 fields. Falls back to `placeholder`
   * when omitted; a compact checkbox has no natural header text, so it
   * renders blank unless one is given explicitly. */
  header?: string;
  /** Label text for a 'checkbox' field — shown next to the box normally, or
   * as a tooltip when `compact` is set. */
  label?: string;
  /** 'checkbox' only — renders as a small icon-toggle button (sized to match
   * the row's remove button) instead of a native checkbox + label, so it can
   * sit inline in row 1 without crowding it the way the full label would. */
  compact?: boolean;
  /** 'checkbox' + `compact` only — icon shown in the toggle button. */
  icon?: string;
  /** Options for 'picker' and 'select' (both SearchableSelect), or
   * 'picker-or-text' (SearchableSelect with a manual-entry escape hatch) fields. */
  options?: { value: string; label: string }[];
  /** Static unit label shown as the placeholder/suffix for a number field, e.g. "yds". */
  unit?: string;
  /** 'number-unit' only — the companion `values` key holding the chosen unit,
   *  rendered as a select appended to the number input so the quantity and
   *  its unit read as one value ("100 pcs") instead of two adjacent columns. */
  unitKey?: string;
  /** 'number-unit' only — options for the appended unit select. */
  unitOptions?: { value: string; label: string }[];
  step?: string;
  min?: string;
  /** Overrides this column's default width (90px for most fields, 125px
   * for currency, or flex-grow for the description column) — e.g. shrinking
   * a quantity field to make room for a wider destination picker. */
  width?: number;
  /** Grower column only — caps how wide it may grow. Uncapped by default so
   * it absorbs whatever space the fixed columns leave. */
  maxWidth?: number;
  /** Renders on a second line below the main row's fields — for secondary,
   * per-row metadata (e.g. a bundle-wide toggle + its category override)
   * that would otherwise crowd the primary description/qty/cost row. */
  row?: 1 | 2;
  /** Hide this field for a given row when it returns false — e.g. a category
   * picker that only makes sense while a paired checkbox is unchecked. */
  showIf?: (values: Record<string, string>) => boolean;
  /** 'photo' only — uploads the file and resolves to the string stored in
   * `values[key]` (e.g. a server URL). Receives the row's id so callers can
   * key the upload to it. */
  onUpload?: (file: File, itemId: string) => Promise<string>;
  /** 'photo' only — converts the stored value into a displayable <img src>,
   * e.g. resolving a relative path to an absolute media URL. */
  resolveUrl?: (value: string) => string;
  /** 'picker-or-text' only — selecting this sentinel option switches the row
   * into free-text entry instead of picking one of `options`. */
  otherOptionValue?: string;
  otherOptionLabel?: string;
  /** 'picker-or-text' only — companion `values` key that sticks the row in
   * manual-entry mode once the escape hatch is used. Defaults to `${key}__manual`.
   * Independent of this flag, a value that doesn't match any option also
   * renders as free text (e.g. data loaded before an option existed). */
  manualEntryKey?: string;
  /** 'readonly' only — renders a node instead of the raw string, e.g. to mask
   *  a sensitive value (payment reference) behind a reveal toggle. Receives
   *  the field's own value plus the row's full values for context. */
  renderValue?: (value: string, values: Record<string, string>) => React.ReactNode;
}

export interface LineItem {
  id: string;
  values: Record<string, string>;
}

interface LineItemsEditorProps {
  items: LineItem[];
  fields: LineItemFieldConfig[];
  onChange: (items: LineItem[]) => void;
  addLabel?: string;
  emptyDefaults?: Record<string, string>;
  genId?: () => string;
  /** Disables every field and hides add/remove controls — e.g. once a quote
   * or order is no longer editable. */
  disabled?: boolean;
  /** Shown in place of the add-row when `disabled` is true. */
  disabledMessage?: string;
  /** Set false when the caller renders its own add-item UI separately (e.g.
   * an explicit-commit "new row" form) instead of this component's
   * append-a-blank-row button. Defaults to true. */
  showAddButton?: boolean;
  /** Optional read-only total column per row, e.g. quantity × unit cost. */
  total?: {
    compute: (values: Record<string, string>) => number;
    format?: (value: number) => string;
  };
  /** Summed footer under the rows. Defaults to true when `total` is set; pass
   *  false when the caller shows the sum somewhere else (procurement puts its
   *  subtotal beside the invoice total instead). */
  showFooterTotal?: boolean;
  /** Footer row's label. Defaults to 'Total'. */
  footerLabel?: string;
  /** Renders the whole section — heading, "N items" count, an Add button in
   *  the header, and the total summary box — instead of leaving each page to
   *  rebuild that chrome inline. Quotes, procurement orders and vendor
   *  bookings each had their own copy, which is why they drifted apart. */
  section?: {
    title: string;
    /** Label for the summary box under the rows. Omit to hide it. */
    summaryLabel?: string;
  };
  /** Symbol shown on every 'currency' field's row (CurrencyInput otherwise
   * uses the project currency) — pass the order/quote's actual currency so item prices
   * don't silently show the wrong symbol on a CNY or USD order. */
  currencySymbol?: string;
  /**
   * Per-row trailing button, shown only while `disabled` — it occupies the
   * same fixed icon column the remove button uses when the list IS editable,
   * so the two can never collide and the header's spacer stays correct.
   *
   * For read-only lists whose rows are real records elsewhere (an order's
   * recorded PY-### payments, say): without it, rendering those rows here
   * would mean dropping the click-through to the record they came from.
   */
  rowAction?: {
    icon: string;
    title: string | ((item: LineItem) => string);
    onClick: (item: LineItem) => void;
  };
  /** Opts a two-column row out of `.lie-row--narrow`'s max-width cap on the
   *  grower column. That cap exists because a narrow row (e.g. Item + Qty)
   *  usually has nothing else to share leftover width with, so the grower
   *  column stopping short reads as intentional restraint — but a row whose
   *  grower is a picker/text field with real long values (a product name, a
   *  bundle line) needs the space capping would otherwise take away. */
  wideGrower?: boolean;
}

const defaultGenId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
/** Row and footer totals follow the editor's own currencySymbol. This used
 *  to hardcode ₦ regardless, so a GBP booking rendered "£35" in the unit-cost
 *  field and "₦35" in the total column of the same row — the one place the
 *  two figures are meant to agree. */
const defaultFormatTotal = (symbol?: string) => (value: number) =>
  symbol ? `${symbol}${value.toLocaleString(getAppLocale())}` : CURRENCY_NOT_SET;

/** 'photo' field renderer — owns its own local uploading spinner state since
 * that's transient UI, not row data the caller needs to track. */
function PhotoField({
  value, disabled, itemId, onUpload, resolveUrl, onChange,
}: {
  value: string;
  disabled: boolean;
  itemId: string;
  onUpload: (file: File, itemId: string) => Promise<string>;
  resolveUrl?: (value: string) => string;
  onChange: (value: string) => void;
}) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      const url = await onUpload(file, itemId);
      onChange(url);
    } catch {
      // Upload failures are silent here — same behavior as the previous
      // per-caller implementations; the row just keeps its placeholder.
    } finally {
      setUploading(false);
    }
  };

  return (
    <label className="lie-photo-btn lie-photo-col" title="Upload photo">
      <input
        type="file"
        accept="image/*"
        className="u-hidden"
        disabled={disabled}
        onChange={e => { const f = e.target.files?.[0]; if (f) void handleFile(f); }}
      />
      {value ? (
        <img src={resolveUrl ? resolveUrl(value) : value} alt="" className="lie-photo-thumb" />
      ) : (
        <span className="lie-photo-placeholder">{uploading ? '…' : '+'}</span>
      )}
    </label>
  );
}

/**
 * One column's width, used by BOTH the header cell and the field beneath it.
 * Kept as a single function on purpose: the header and the data row each had
 * their own ladder, and any type the two ladders disagreed on (currency,
 * compact checkbox, photo) shifted every column after it out of alignment
 * with its own label. Widths that live in CSS rather than here must stay
 * excluded from .lie-row's positional width rule, or the two fight.
 */
function columnStyle(field: LineItemFieldConfig, growerKey?: string): React.CSSProperties {
  // 24, not 32: .lie-row > .lie-photo-btn is 24px, and the header reserving
  // 32 pushed every following column 8px out of line with its own field.
  if (field.type === 'photo') return { flex: '0 0 24px', width: 24 };
  if (field.width) return { flex: `0 0 ${field.width}px`, width: field.width };
  // minWidth 160, not 0: at 0 this column had nothing stopping it shrinking
  // to near-zero once the row's fixed-width columns didn't all fit on one
  // line (e.g. ~900px viewport). .lie-editor scrolls horizontally
  // (overflow-x: auto), so past this floor the row should scroll instead of
  // crushing the grower column. The 640px stacked mobile layout overrides
  // this back to flex-basis 100% / no cap, so it doesn't fight there.
  if (field.key === growerKey) return { flex: '1 1 0', minWidth: 160, ...(field.maxWidth ? { maxWidth: field.maxWidth } : {}) };
  if (field.type === 'number-unit') return { flex: '0 0 155px', width: 155 };
  if (field.type === 'currency') return { flex: '0 0 125px', width: 125 };
  if (field.type === 'checkbox' && field.compact) return { flex: '0 0 28px', width: 28 };
  return { flex: '0 0 90px', width: 90 };
}

function renderField(
  field: LineItemFieldConfig,
  values: Record<string, string>,
  disabled: boolean,
  itemId: string,
  updateItem: (patch: Record<string, string>) => void,
  currencySymbol?: string,
  // The column that should grow to fill leftover row width — passed down so
  // this data-row sizing can't drift from the header's own column widths the
  // way it used to (see growerKey's computation below for why it can't just
  // be "the first field").
  growerKey?: string,
) {
  // Same function the header row uses — the two used to be separate ladders
  // that agreed on the grower and number-unit and disagreed on everything
  // else: the header reserved 125px for a currency column while the field
  // itself fell through to `undefined` and sized to its content, and a
  // compact checkbox reserved 28px against a 90px field. Every column after
  // the first mismatch was pushed out of line with its own label.
  const widthStyle = columnStyle(field, growerKey);
  // A showIf-hidden field used to `return null` outright, which removed it
  // from the row's flex layout entirely — every column after it then
  // shifted left to fill the gap while the (unconditional) header above
  // stayed put, so the whole row after that point read as misaligned with
  // its own headers. Rendering an empty placeholder at the same width
  // keeps the column's space reserved, the same way an always-hidden field
  // in a caller's config still gets to keep its header lined up.
  if (field.showIf && !field.showIf(values)) {
    return <span key={field.key} className="lie-field-placeholder" style={widthStyle} />;
  }
  const value = values[field.key] ?? '';

  if (field.type === 'photo') {
    if (!field.onUpload) return null;
    return (
      <PhotoField
        key={field.key}
        value={value}
        disabled={disabled}
        itemId={itemId}
        onUpload={field.onUpload}
        resolveUrl={field.resolveUrl}
        onChange={v => updateItem({ [field.key]: v })}
      />
    );
  }
  if (field.type === 'picker-or-text') {
    const manualKey = field.manualEntryKey ?? `${field.key}__manual`;
    const options = field.options ?? [];
    // Always shows the picker (matches the Vendor field pattern) — even
    // for a value that doesn't match any option (e.g. a legacy free-text
    // entry, or one typed via the "+ Create" row below), so the current
    // name is never silently swapped out for a bare, un-searchable text
    // input the way it used to be.
    // A value that matches no option (a legacy free-text name, or one just
    // created via "+ Create") still has to appear in the list — otherwise the
    // trigger shows it as selected but searching the dropdown can never find
    // it, which reads as the current selection having vanished.
    const withCurrent = value && !options.some(o => o.value === value)
      ? [{ value, label: value }, ...options]
      : options;
    return (
      <SearchableSelect
        key={field.key}
        value={value}
        onChange={v => {
          if (field.otherOptionValue && v === field.otherOptionValue) {
            updateItem({ [manualKey]: 'true', [field.key]: '' });
          } else {
            updateItem({ [field.key]: v, [manualKey]: 'false' });
          }
        }}
        onCreateNew={query => updateItem({ [field.key]: query, [manualKey]: 'true' })}
        options={field.otherOptionValue
          ? [...withCurrent, { value: field.otherOptionValue, label: field.otherOptionLabel ?? '+ New…' }]
          : withCurrent}
        placeholder={field.placeholder}
        disabled={disabled}
        style={widthStyle}
      />
    );
  }
  if (field.type === 'picker') {
    const opts = field.options ?? [];
    return (
      <SearchableSelect
        key={field.key}
        value={value}
        onChange={v => updateItem({ [field.key]: v })}
        // Same reason as picker-or-text: never show a selected value the
        // dropdown itself can't list.
        options={value && !opts.some(o => o.value === value) ? [{ value, label: value }, ...opts] : opts}
        placeholder={field.placeholder}
        disabled={disabled}
        style={widthStyle}
      />
    );
  }
  if (field.type === 'select') {
    return (
      <SearchableSelect
        key={field.key}
        className="form-input form-input--sm"
        value={value}
        disabled={disabled}
        style={widthStyle}
        onChange={v => updateItem({ [field.key]: v })}
        options={field.options ?? []}
      />
    );
  }
  if (field.type === 'number-unit') {
    const unitKey = field.unitKey ?? `${field.key}__unit`;
    return (
      <div key={field.key} className="lie-unit-field" style={widthStyle}>
        <input
          type="number"
          min={field.min ?? '0'}
          step={field.step ?? '1'}
          className="lie-unit-number"
          placeholder={field.placeholder}
          value={value}
          disabled={disabled}
          onChange={e => updateItem({ [field.key]: e.target.value })}
        />
        <SearchableSelect
          className="lie-unit-select"
          value={values[unitKey] ?? ''}
          disabled={disabled}
          onChange={v => updateItem({ [unitKey]: v })}
          options={field.unitOptions ?? []}
        />
      </div>
    );
  }
  if (field.type === 'richtext') {
    return (
      <div key={field.key} className="lie-richtext-field" style={widthStyle}>
        <RichTextEditor
          value={value}
          onChange={v => updateItem({ [field.key]: v })}
          placeholder={field.placeholder}
          disabled={disabled}
        />
      </div>
    );
  }
  if (field.type === 'number') {
    return (
      <input
        key={field.key}
        type="number"
        min={field.min ?? '0'}
        step={field.step ?? '1'}
        className="form-input form-input--sm lie-number-input"
        placeholder={field.unit ?? field.placeholder}
        value={value}
        disabled={disabled}
        onChange={e => updateItem({ [field.key]: e.target.value })}
        style={widthStyle}
      />
    );
  }
  if (field.type === 'currency') {
    return (
      <CurrencyInput
        key={field.key}
        className="form-input form-input--sm lie-currency-input"
        wrapperStyle={widthStyle}
        symbol={currencySymbol}
        min={field.min ?? '0'}
        placeholder={field.placeholder}
        value={value}
        disabled={disabled}
        onChange={e => updateItem({ [field.key]: e.target.value })}
      />
    );
  }
  if (field.type === 'date') {
    return (
      <input
        key={field.key}
        type="date"
        className="form-input form-input--sm"
        value={value}
        disabled={disabled}
        style={widthStyle}
        onChange={e => updateItem({ [field.key]: e.target.value })}
      />
    );
  }
  if (field.type === 'checkbox') {
    const checked = value === 'true';
    if (field.compact) {
      return (
        <button
          key={field.key}
          type="button"
          className={`lie-toggle-btn${checked ? ' active' : ''}`}
          title={field.label ?? field.placeholder}
          aria-pressed={checked}
          disabled={disabled}
          onClick={() => updateItem({ [field.key]: checked ? 'false' : 'true' })}
        >
          <Icon name={field.icon ?? 'link'} size={14} />
        </button>
      );
    }
    return (
      <label key={field.key} className="lie-checkbox-field">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={e => updateItem({ [field.key]: e.target.checked ? 'true' : 'false' })}
        />
        {field.label ?? field.placeholder}
      </label>
    );
  }
  if (field.type === 'readonly') {
    // Plain text, not an input — this row's fact was never editable anywhere
    // (e.g. a courier's own scraped status/dates), so an input-shaped field
    // would imply a save path that doesn't exist.
    return (
      <span key={field.key} className="lie-readonly-field" style={widthStyle}>
        {field.renderValue ? field.renderValue(value, values) : (value || '—')}
      </span>
    );
  }
  return (
    <input
      key={field.key}
      type="text"
      className="form-input form-input--sm"
      placeholder={field.placeholder}
      value={value}
      disabled={disabled}
      style={widthStyle}
      onChange={e => updateItem({ [field.key]: e.target.value })}
    />
  );
}

/**
 * Add/edit/remove row list — used for line items across the app (bundle
 * fabrics, quote items, procurement items, etc). Each screen owns its own
 * row shape (via `fields`) and persistence (via `onChange` + its own save
 * call) since bundles/quotes/procurement orders each save line items
 * completely differently (JSON blob vs. per-row API call vs. multi-target
 * record creation) — this component only renders and edits the row list.
 *
 * Fields with `row: 2` render on a secondary line below the primary row —
 * for per-row metadata (like a bundle-wide toggle and its category
 * override) that would otherwise crowd the main description/qty/cost row.
 */
export default function LineItemsEditor({
  items, fields, onChange, addLabel = 'Add item', emptyDefaults = {}, genId = defaultGenId,
  disabled = false, disabledMessage, total, showAddButton = true, currencySymbol: currencySymbolProp, section,
  showFooterTotal = true, footerLabel = 'Total', rowAction, wideGrower = false,
}: LineItemsEditorProps) {
  // The order/quote's own symbol wins; otherwise the project currency; with
  // neither, amounts read "currency not set" rather than guessing a symbol.
  const money = useMoney();
  const currencySymbol = currencySymbolProp || (money.isSet ? money.symbol : undefined);
  const addItem = () => {
    const values: Record<string, string> = {};
    for (const f of fields) values[f.key] = emptyDefaults[f.key] ?? '';
    onChange([...items, { id: genId(), values }]);
  };

  const removeItem = (id: string) => onChange(items.filter(it => it.id !== id));

  const updateItem = (id: string, patch: Record<string, string>) =>
    onChange(items.map(it => it.id === id ? { ...it, values: { ...it.values, ...patch } } : it));

  // A field with `showIf` (e.g. Decor Item, only relevant when
  // destination_type === 'decor') already hides its own control per-row via
  // renderField — but the column itself, header included, stayed reserved
  // even when NOT ONE row in the whole list needed it, leaving a permanently
  // empty column. Drop it from mainFields entirely in that case; a field
  // still shows the moment any row's showIf turns true, since this
  // recomputes on every render from the live items/values.
  const mainFields = fields
    .filter(f => (f.row ?? 1) === 1)
    .filter(f => !f.showIf || items.some(it => f.showIf!(it.values)));
  const metaFields = fields.filter(f => f.row === 2);
  const formatTotal = total?.format ?? defaultFormatTotal(currencySymbol);

  // Column widths mirror .lie-row's own CSS so headers line up with the
  // fields they label — kept in sync here rather than duplicated in CSS
  // since it already depends on field order/type the same way renderField does.
  // The growing (flex: 1) column is the first non-photo field *that doesn't
  // already have an explicit width* — matches ".lie-row > *:first-child" only
  // when that first field is width-less, same as every existing caller's
  // config. A config that puts an explicit width on its first field (e.g. a
  // short Label column ahead of a free-text Address column) would otherwise
  // leave nothing actually growing: the field's own inline width pins it in
  // place, but the CSS class rule still targeted it purely by position, and
  // the real grower candidate (Address) fell through to the generic 90px
  // rule and read as squashed. Passed into renderField below so the data-row
  // gets the identical inline flex — previously only the header used this
  // value, so an explicit-first-field config drifted the header and body
  // widths apart instead of both just being wrong the same way.
  const growerKey = mainFields.find(f => f.type !== 'photo' && !f.width)?.key;

  // Only a two-column row (e.g. bundle items: Item + Qty) leaves the grower
  // column with nothing else to share leftover width with — that's the case
  // .lie-row--narrow's max-width cap (LineItemsEditor.css) exists for. A row
  // with more real columns (VendorBookingPanel's Description + Qty + Unit
  // cost + Event, etc.) already has other fixed-width fields to keep the
  // ratio sane, so it's left uncapped and fills the leftover space instead
  // of stopping short and leaving a gap. Computed once here and applied to
  // the header/body/footer rows alike so all three stay aligned — mirrors
  // how growerKey itself is threaded through rather than recomputed per row.
  const isNarrowRow = mainFields.length <= 2 && !wideGrower;
  const rowClass = isNarrowRow ? ' lie-row--narrow' : '';

  const editor = (
    <div className="lie-editor">
      {items.length > 0 && (
        <div className={`lie-row lie-header-row${rowClass}`}>
          {mainFields.map(field => (
            <span
              key={field.key}
              // A photo column's header cell needs the same structural
              // marker the real button carries in the data row below (kept
              // separate from .lie-photo-btn's visual styling, which a bare
              // header <span> shouldn't take on) — the grower column's
              // max-width comes from a CSS sibling selector keyed off that
              // class (`.lie-photo-col + *`), and without it here the
              // header's grower cell matched no cap at all and grew to the
              // row's full width while the field beneath it stopped at
              // 26rem, dragging every column after it out of line with its
              // own label.
              className={`lie-header-cell${field.type === 'photo' ? ' lie-photo-col' : ''}`}
              style={columnStyle(field, growerKey)}
            >
              {field.header ?? (field.compact ? '' : field.placeholder)}
            </span>
          ))}
          {total && <span className="lie-header-cell lie-total lie-total-fixed">Total</span>}
          {(!disabled || !!rowAction) && <span className="lie-header-cell lie-spacer-col" />}
        </div>
      )}
      {items.map(item => (
        <div key={item.id} className="lie-item">
          <div className={`lie-row${rowClass}`}>
            {mainFields.map(field => renderField(field, item.values, disabled, item.id, patch => updateItem(item.id, patch), currencySymbol, growerKey))}
            {total && <span className="lie-total">{formatTotal(total.compute(item.values))}</span>}
            {!disabled ? (
              <button type="button" className="lie-remove-btn" onClick={() => removeItem(item.id)} title="Remove">
                <Icon name="trash" size={14} />
              </button>
            ) : rowAction ? (
              /* Same fixed icon column the remove button occupies, so a
                 read-only list keeps its rows aligned with the header. */
              <button
                type="button"
                className="lie-remove-btn"
                onClick={() => rowAction.onClick(item)}
                title={typeof rowAction.title === 'function' ? rowAction.title(item) : rowAction.title}
              >
                <Icon name={rowAction.icon} size={14} />
              </button>
            ) : null}
          </div>
          {metaFields.length > 0 && (
            <div className="lie-meta-row">
              {metaFields.map(field => renderField(field, item.values, disabled, item.id, patch => updateItem(item.id, patch), currencySymbol))}
            </div>
          )}
        </div>
      ))}
      {/* Summed footer, shown whenever the editor has a per-row total column.
          The individual row totals were always there but never added up, so
          reading a quote's or an order's bottom line meant doing the sum by
          eye — including the negative discount rows, which is exactly where
          that goes wrong. Lives here rather than in each consumer so every
          line-item list totals the same way. */}
      {total && showFooterTotal && items.length > 0 && (
        <div className={`lie-row lie-footer-row${rowClass}`}>
          {mainFields.map(field => (
            <span key={field.key} className="lie-footer-cell" style={columnStyle(field, growerKey)}>
              {field.key === growerKey ? footerLabel : ''}
            </span>
          ))}
          <span className="lie-footer-cell lie-total lie-total-fixed">
            {formatTotal(items.reduce((sum, it) => sum + total.compute(it.values), 0))}
          </span>
          {!disabled && <span className="lie-footer-cell lie-spacer-col" />}
        </div>
      )}
      {disabled ? (
        disabledMessage && <p className="lie-disabled-hint">{disabledMessage}</p>
      ) : showAddButton && !section && (
        <button type="button" className="lie-add-btn" onClick={addItem}>
          <Icon name="plus" size={12} /> {addLabel}
        </button>
      )}
    </div>
  );

  if (!section) return editor;

  // Full section: heading + count + Add in the header, rows, then the summary
  // box. Owning this here is the point — every page that shows line items now
  // gets the identical treatment by passing values, not by rebuilding it.
  const grandTotal = total ? items.reduce((sum, it) => sum + total.compute(it.values), 0) : 0;
  return (
    <div className="lie-section">
      <div className="lie-section-header">
        <h4 className="form-section-title">{section.title}</h4>
        <div className="lie-section-header-right">
          <span className="lie-section-count">
            {items.length} {items.length === 1 ? 'item' : 'items'}
          </span>
          {!disabled && (
            <button type="button" className="lie-add-btn" onClick={addItem}>
              <Icon name="plus" size={12} /> {addLabel}
            </button>
          )}
        </div>
      </div>
      {editor}
      {section.summaryLabel && total && items.length > 0 && (
        <div className="lie-summary">
          <div className="lie-summary-field">
            <span className="lie-summary-label">{section.summaryLabel}</span>
            <div className="lie-summary-box">
              <span className="lie-summary-currency">{currencySymbol ?? CURRENCY_NOT_SET}</span>
              {currencySymbol && <span className="lie-summary-amount">{grandTotal.toLocaleString(getAppLocale())}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
